import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Grid } from "@material-ui/core";
import { useSelector } from "react-redux";
import { endpoint } from "assets/api/endpoint";
import { apiHandler } from "assets/api";
import CircularProgresss from "components/CircularProgress/CircularProgresss";
import SubHeader from "components/UpdateUi/SubHeader/SubHeader";
import CustomeTextField from "components/UpdateUi/CustomeTextField";
import ToastModal from "views/components/ToastModal";
import { handleOnlyNumberAndPointer } from "utils/Utils";
import { parseDate } from "utils/Utils";

const FoirConfigCreate = ({ props, classes }) => {
  const navigate = useNavigate();
  const params = useParams();
  const { authToken } = useSelector((state) => state.login.authData);
  const [minLoanAmount, setMinLoanAmount] = useState("");
  const [maxLoanAmount, setMaxLoanAmount] = useState("");
  const [foirPercentage, setFoirPercentage] = useState("");
  const [minLoanAmountState, setMinLoanAmountState] = useState(false);
  const [maxLoanAmountState, setMaxLoanAmountState] = useState(false);
  const [foirPercentageState, setFoirPercentageState] = useState(false);
  const [callInProgress, setCallInProgress] = useState(false);
  const [toast, setToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [toastMessageType, setToastMessageType] = useState("");
  const [redirectURL, setRedirectURL] = useState("");

  const toastUpdate = (type, message, showToast) => {
    setToastMessageType(type);
    setToastMessage(message);
    setToast(showToast);
    if (redirectURL !== "") {
      setRedirectURL("");
      navigate(redirectURL);
    }
  };
  useEffect(() => {
    if (params?.foirId) {
      GetData();
    }
  }, []);
  const GetData = async () => {
    setCallInProgress(true);
    const result = await apiHandler({
      url: endpoint.FOIR_CONFIG_FIND_BY_ID + params.foirId,
      authToken,
    });
    setCallInProgress(false);
    if (result?.data?.error_code) {
      toastUpdate("Error", result?.data?.user_description, true);
    } else {
      setMinLoanAmount(result?.data?.minLoanAmount || "");
      setMaxLoanAmount(result?.data?.maxLoanAmount || "");
      setFoirPercentage(result?.data?.foirPercentage || "");
    }
  };
  const isValidated = () => {
    let valid = true;

    if (!minLoanAmount) {
      setMinLoanAmountState(true);
      valid = false;
    }

    if (!maxLoanAmount) {
      setMaxLoanAmountState(true);
      valid = false;
    }

    if (!foirPercentage) {
      setFoirPercentageState(true);
      valid = false;
    }

    if (!valid) return false;

    if (Number(minLoanAmount) >= Number(maxLoanAmount)) {
      toastUpdate(
        "Error",
        "Minimum Income must be less than Maximum Income",
        true
      );
      return false;
    }

    if (Number(foirPercentage) < 0 || Number(foirPercentage) > 100) {
      toastUpdate("Error", "FOIR Percentage should be between 0 and 100", true);
      return false;
    }

    return true;
  };
  
  const effectiveFrom = new Date();
  const effectiveTo = new Date();
  effectiveTo.setFullYear(effectiveTo.getFullYear() + 5);

  const saveFoirInformation = async () => {
    setCallInProgress(true);
    const res = await apiHandler({
      url: endpoint.FOIR_CONFIG_CREATE,
      method: "POST",
      authToken,
      data: {
        minLoanAmount: Number(minLoanAmount),
        maxLoanAmount: Number(maxLoanAmount),
        foirPercentage: Number(foirPercentage),
        effectiveFrom: parseDate(effectiveFrom),
        effectiveTo: parseDate(effectiveTo),
        isActive: true,
      },
    });
    setCallInProgress(false);
    if (res?.data?.error_code) {
      toastUpdate("Error", res?.data?.user_description, true);
      return;
    }
    return "SUCCESS";
  };
  const updateFoirInformation = async () => {
    setCallInProgress(true);
    const res = await apiHandler({
      url: endpoint.FOIR_CONFIG_UPDATE + params.foirId,
      method: "PUT",
      authToken,
      data: {
        minLoanAmount: Number(minLoanAmount),
        maxLoanAmount: Number(maxLoanAmount),
        foirPercentage: Number(foirPercentage),
        effectiveFrom: parseDate(effectiveFrom),
        effectiveTo: parseDate(effectiveTo),
        isActive: true,
      },
    });
    setCallInProgress(false);
    if (res?.data?.error_code) {
      toastUpdate("Error", res?.data?.user_description, true);
      return "";
    }
    return "SUCCESS";
  };
  const handleSubmit = async () => {
    if (!isValidated()) return;

    const rangeValid = await validateRangeOverlap();
    if (!rangeValid) return;
    let status = "";
    if (params?.foirId) {
      status = await updateFoirInformation();
    } else {
      status = await saveFoirInformation();
    }
    if (status === "SUCCESS") {
      toastUpdate(
        "Success",
        params?.foirId
          ? "FOIR Configuration updated successfully!"
          : "FOIR Configuration created successfully!",
        true
      );
      setTimeout(() => {
        navigate("/coreservice/foirConfig");
      }, 1000);
    }
  };
  const handleCancel = () => {
    navigate("/coreservice/foirConfig");
  };
  const validateRangeOverlap = async () => {
    const result = await apiHandler({
      url: endpoint.FOIR_CONFIG_FINDALL,
      authToken,
    });
    if (result?.data?.error_code) {
      return true;
    }
    const currentMin = Number(minLoanAmount);
    const currentMax = Number(maxLoanAmount);
    const overlap = result.data.some((item) => {
      if (params?.foirId && Number(item.id) === Number(params.foirId)) {
        return false;
      }
      const existingMin = Number(item.minLoanAmount);
      const existingMax = Number(item.maxLoanAmount);
      return currentMin <= existingMax && currentMax >= existingMin;
    });
    if (overlap) {
      toastUpdate(
        "Error",
        "Income range overlaps with an existing FOIR configuration",
        true
      );
      return false;
    }
    return true;
  };
  return (
    <Grid container>
      <Grid item xs={12} sm={12} md={12}>
        <SubHeader
          label={
            params?.foirId
              ? "Edit FOIR Configuration"
              : "Create FOIR Configuration"
          }
          button1="Save"
          button2="Cancel"
          button1click={handleSubmit}
          button2click={handleCancel}
        />
      </Grid>
      <Grid item xs={12} sm={12} md={12} className={classes.inputContainer}>
        <Grid container className={classes.inputFieldContainer}>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomeTextField
              label="Minimum Income (₹)"
              required
              value={minLoanAmount}
              error={minLoanAmountState}
              setState={(e) => handleOnlyNumberAndPointer(e, setMinLoanAmount)}
              setError={setMinLoanAmountState}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomeTextField
              label="Maximum Income (₹)"
              required
              value={maxLoanAmount}
              error={maxLoanAmountState}
              setState={(e) => handleOnlyNumberAndPointer(e, setMaxLoanAmount)}
              setError={setMaxLoanAmountState}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} className={classes.fieldContaienr}>
            <CustomeTextField
              label="Foir (%)"
              required
              value={foirPercentage}
              error={foirPercentageState}
              setState={(e) => handleOnlyNumberAndPointer(e, setFoirPercentage)}
              setError={setFoirPercentageState}
            />
          </Grid>
        </Grid>
      </Grid>
      {toast && (
        <ToastModal
          toastMessage={toastMessage}
          toastMessageType={toastMessageType}
          toastUpdate={toastUpdate}
        />
      )}

      {callInProgress && <CircularProgresss callInProgress={callInProgress} />}
    </Grid>
  );
};

export default FoirConfigCreate;
